import { DoneActorEvent } from 'xstate';
import { AuthTransport } from './transport';

export interface AuthMachineContext {
  /**
   * Header injection for the auth HTTP calls, supplied by the host when the
   * actor is created. Injected rather than imported — see `./transport`.
   * Absent → the hardcoded pre-CTAP-1159 defaults apply.
   */
  transport?: AuthTransport;
  // user: {
  //   email: string;
  //   username: string;
  //   firstName: string;
  //   lastName: string;
  //   birthdate?: Date;
  //   gender?: string /** @todo use enum? */;
  //   createdAt: Date;
  //   updatedAt: Date;
  //   role?: string /** @todo use enum? */;
  //   locked: boolean;
  //   affiliateId: string;
  //   stripeId: string;
  // } | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: Record<string, any> | null;
  accessToken: string;
  refreshToken: string;
  ignoreRefreshToken: boolean;
  /**
   * Why the session ended, so `unauthenticating` (entered by BOTH a deliberate
   * LOGOUT and a client-side refresh failure) can route the two differently:
   * `deliberate` → `loggedOut.afterLogout` (home), `expired` →
   * `loggedOut.afterRefreshFailure` (return-path redirect). See CTAP-1129.
   */
  logoutReason: 'deliberate' | 'expired' | null;
  endpoints: {
    login: string;
    logout: string;
    refresh: string;
    ssrRefresh: string;
  };
  errors: Array<string>;
}

export interface RefreshTokenEvent {
  type: 'REFRESH_TOKEN';
  accessToken: AuthMachineContext['accessToken'];
}

export interface LoginEvent {
  type: 'LOGIN';
  username: string;
  password: string;
}

export interface LogoutEvent {
  type: 'LOGOUT';
}

/**
 * The SERVER rejected our access token (a 401 from any authenticated request).
 * Distinct from LOGOUT: an expired session is not a chosen sign-out, so it must
 * route to `afterRefreshFailure` — which keeps the return path to wherever the
 * visitor was — rather than `afterLogout`, which deliberately goes home with no
 * return path (CTAP-1120/1129/1425).
 */
export interface SessionExpiredEvent {
  type: 'SESSION_EXPIRED';
}

/**
 * A credential-change mutation (email/password) minted a fresh session server-side and returned a
 * new access token; the rotated refresh cookie is already set on the response. Swap the in-memory
 * access token in place so the user stays signed in — no login round-trip.
 */
export interface SessionReissuedEvent {
  type: 'SESSION_REISSUED';
  accessToken: AuthMachineContext['accessToken'];
}

export interface ClearErrorsEvent {
  type: 'CLEAR_ERRORS';
}

export interface RestoreEvent {
  type: 'RESTORE';
}

// export interface AfterLoggedInEvent {
//   type: 'AFTER_LOGGED_IN';
// }

// export interface AfterLoggedOutEvent {
//   type: 'AFTER_LOGGED_OUT';
// }

export interface AuthenticateSuccessEvent {
  type: 'AUTHENTICATE_SUCCESS';
  user: Partial<AuthMachineContext['user']>;
  accessToken: AuthMachineContext['accessToken'];
}

export interface RefreshTokenSuccessEvent {
  type: 'REFRESH_TOKEN_SUCCESS';
  accessToken: AuthMachineContext['accessToken'];
}

export interface UnauthenticateSuccessEvent {
  type: 'UNAUTHENTICATE_SUCCESS';
}

export interface AuthenticatedEmittedEvent {
  type: 'EMIT_AUTHENTICATED';
  accessToken: AuthMachineContext['accessToken'];
  /**
   * The signed-in identity, present when the emit follows an AUTHENTICATE
   * (login) success — identity consumers (GA4 signed_in, Klaviyo identify)
   * need it at exactly this moment. Absent on token refreshes: a refresh
   * establishes no new identity.
   */
  email?: string;
}

export interface UnauthenticatedEmittedEvent {
  type: 'EMIT_UNAUTHENTICATED';
}

// type AuthEvents = RefreshTokenEvent | AnotherEvent;
export type AuthMachineEvents =
  | RefreshTokenEvent
  | RestoreEvent
  | LoginEvent
  | LogoutEvent
  | SessionExpiredEvent
  | SessionReissuedEvent
  | ClearErrorsEvent
  // | AfterLoggedInEvent
  // | AfterLoggedOutEvent
  | DoneActorEvent<AuthenticateSuccessEvent>
  | DoneActorEvent<RefreshTokenSuccessEvent>;

export type AuthMachineEmittedEvents =
  AuthenticatedEmittedEvent | UnauthenticatedEmittedEvent;

export enum AuthMachineStatusCode {
  INCORRECT_LOGIN_CREDENTIALS = 'INCORRECT_LOGIN_CREDENTIALS',
  /**
   * The account is TEMPORARILY locked from signing in after repeated failed
   * password attempts (e.g. Magento's lockout window, surfaced to the user
   * as "Sign-in with this account is unavailable"). It clears on its own —
   * hosts branch on this to run a recovery flow (an OTP re-verify step, a
   * "try again later" notice) instead of the incorrect-credentials copy.
   *
   * Mapped from the login response's structured `code` field
   * ({@link AuthenticateResponse.code}) — never from the display message,
   * which belongs to the backend (Magento's copy can change). A backend that
   * doesn't emit the code keeps collapsing to INCORRECT_LOGIN_CREDENTIALS.
   */
  ACCOUNT_LOCKED = 'ACCOUNT_LOCKED',
}
